import { Input, Label, Switch, Textarea } from "@devhop/ui";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

type FormMetadataSectionProps = {
  formName: string;
  setFormName: (value: string) => void;
  categoryId: string | null;
  setCategoryId: (value: string | null) => void;
  departmentId: string | null;
  setDepartmentId: (value: string | null) => void;
  description: string;
  setDescription: (value: string) => void;
  useCase: string;
  setUseCase: (value: string) => void;
  targetAudience: string;
  setTargetAudience: (value: string) => void;
  processAfterSubmission: string;
  setProcessAfterSubmission: (value: string) => void;
  allowFileAttachment: boolean;
  setAllowFileAttachment: (value: boolean) => void;
};

type CategoryItem = { id: string; name: string };
type DepartmentItem = { id: string; name: string };

export function FormMetadataSection({
  formName,
  setFormName,
  categoryId,
  setCategoryId,
  departmentId,
  setDepartmentId,
  description,
  setDescription,
  useCase,
  setUseCase,
  targetAudience,
  setTargetAudience,
  processAfterSubmission,
  setProcessAfterSubmission,
  allowFileAttachment,
  setAllowFileAttachment,
}: FormMetadataSectionProps) {
  return (
    <div className="space-y-4" data-tourid="form-metadata">
      <h3 className="mb-4 font-semibold">ຂໍ້ມູນຟອມ</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="form-name">ຊື່ຟອມ *</Label>
          <Input
            id="form-name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="ຕົວຢ່າງ: ຟອມລາພັກ"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="form-category">ປະເພດຟອມ</Label>
          <InfiniteCombobox<CategoryItem>
            queryKey={["form-categories", "metadata"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/forms/categories/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/forms/categories/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={categoryId || ""}
            onValueChange={(val) => setCategoryId(val || null)}
            placeholder="ເລືອກປະເພດ..."
            className="mt-1"
            clearable
          />
        </div>

        <div>
          <Label htmlFor="form-department">ພະແນກ</Label>
          <InfiniteCombobox<DepartmentItem>
            queryKey={["form-departments", "metadata"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentId || ""}
            onValueChange={(val) => setDepartmentId(val || null)}
            placeholder="ເລືອກພະແນກ..."
            className="mt-1"
            clearable
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="allow-file">ອະນຸຍາດແນບແຟ້ມ</Label>
          <Switch
            id="allow-file"
            checked={allowFileAttachment}
            onCheckedChange={setAllowFileAttachment}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="form-description">ຄຳອະທິບາຍສັ້ນ</Label>
        <Textarea
          id="form-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="ອະທິບາຍສັ້ນໆ ກ່ຽວກັບຟອມນີ້..."
          className="mt-1"
          rows={2}
        />
      </div>

      <div>
        <Label htmlFor="form-use-case">ໃຊ້ກັບກໍລະນີໃດ</Label>
        <Textarea
          id="form-use-case"
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
          placeholder="ອະທິບາຍວ່າຄວນໃຊ້ຟອມນີ້ເມື່ອໃດ..."
          className="mt-1"
          rows={2}
        />
      </div>

      <div>
        <Label htmlFor="form-target">ໃຜຄວນໃຊ້</Label>
        <Input
          id="form-target"
          value={targetAudience}
          onChange={(e) => setTargetAudience(e.target.value)}
          placeholder="ຕົວຢ່າງ: ພະນັກງານທັງໝົດ, ຫົວໜ້າພະແນກ..."
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="form-process">ຂັ້ນຕອນຫຼັງສົ່ງຟອມ</Label>
        <Textarea
          id="form-process"
          value={processAfterSubmission}
          onChange={(e) => setProcessAfterSubmission(e.target.value)}
          placeholder="ອະທິບາຍຂັ້ນຕອນຫຼັງສົ່ງຟອມ (ໄປຫາໃຜ, ການປະມວນຜົນ)..."
          className="mt-1"
          rows={2}
        />
      </div>
    </div>
  );
}
